import * as React from 'react';
import { X } from '@phosphor-icons/react';
import { Button } from '../../ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';

export interface BulkAction {
  label: string;
  /** Receives the click so a caller can return focus to this button later. */
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Defaults to 'outline' — pass 'destructive' for a delete-style action. */
  variant?: 'outline' | 'destructive';
  /** Why the action can't run; it stays visible, focusable and inert. */
  reason?: string;
}

export interface BulkActionToolbarProps {
  /** Number of currently-selected rows. */
  count: number;
  /** Noun after the count, e.g. "3 selected" / "3 shortlisted". Defaults to
   *  'selected' — reads correctly regardless of which actions are supplied
   *  (including destructive ones), unlike a more specific default would. */
  noun?: string;
  actions: BulkAction[];
  onClear: () => void;
}

/**
 * The bulk-action row a table's search/filter bar swaps to once rowSelection
 * has a non-empty selection — drop it in as StickyTableToolbar's `children`
 * when `selectedIds.size > 0`. Generic over its action list so any table
 * (coupons, roster, creators, ...) can supply its own bulk operations.
 */
export function BulkActionToolbar({ count, noun = 'selected', actions, onClear }: BulkActionToolbarProps): React.ReactElement {
  return (
    // h-9, not h-10: matches Input's own height (see ui/input.tsx), which is
    // what the search/filter row this toolbar swaps places with is built
    // from. The two used to differ by 4px, so selecting a row shifted the
    // whole table down by that much as StickyTableToolbar's ResizeObserver
    // re-measured and republished --dt-top-offset off the taller toolbar.
    <div className="flex h-9 items-center justify-between">
      <span className="text-body-sm-medium text-muted-foreground">
        {count} {noun}
      </span>
      <div className="flex items-center gap-2">
        {actions.map((action) => (
          <BulkActionButton key={action.label} action={action} />
        ))}
        <Button variant="ghost" size="icon-sm" onClick={onClear} aria-label="Clear selection">
          <X />
        </Button>
      </div>
    </div>
  );
}

function BulkActionButton({ action }: { action: BulkAction }) {
  const reasonId = React.useId();
  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={action.variant ?? 'outline'}
            size="sm"
            aria-disabled={action.reason ? true : undefined}
            aria-describedby={action.reason ? reasonId : undefined}
            className={action.reason ? 'cursor-not-allowed opacity-50' : undefined}
            onClick={(e) => {
              if (action.reason) return;
              action.onClick(e);
            }}
          >
            {action.label}
          </Button>
        </TooltipTrigger>
        {action.reason && <TooltipContent>{action.reason}</TooltipContent>}
      </Tooltip>
      {action.reason && (
        <span id={reasonId} className="sr-only">
          {action.reason}
        </span>
      )}
    </>
  );
}
